import React from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import {
    Description,
    ErrorMessage,
    Field,
    FieldGroup,
    Fieldset,
    Label,
    RequiredLabel,
} from "src/components/base/fieldset";
import Form from "src/components/base/form";
import { useForm } from "@tanstack/react-form";
import { Button, PrimaryButton } from "src/components/base/button";
import { Input } from "src/components/base/input";
import { Switch, SwitchField } from "src/components/base/switch";
import { ApiInternal } from "src/api/api_internal";

/**
 * The properties for {@link CreateGroupDialog}
 */
export type CreateGroupDialogProps = {
    /** Callback when the popup should be closed */
    onClose: () => void;
};

/**
 * A dialog to create a new local group
 */
export default function CreateGroupDialog(props: CreateGroupDialogProps) {
    const [t] = useTranslation("dialog-create-group");
    const [tg] = useTranslation();

    const form = useForm({
        defaultValues: {
            name: "",
            description: "",
            internet: false,
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                const res = await ApiInternal.groups.local.create({
                    name: value.name,
                    description: value.description,
                    internet: value.internet,
                });

                if (res.result === "Err") {
                    return {
                        fields: {
                            name: res.error.name ? t("error.name-already-occupied") : undefined,
                        },
                    };
                }

                props.onClose();
            },
        },
    });

    return (
        <Dialog open={true} onClose={props.onClose}>
            <DialogTitle>{t("heading.create-group")}</DialogTitle>
            <DialogBody>
                <Form onSubmit={form.handleSubmit}>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field name={"name"}>
                                {(fieldApi) => (
                                    <Field>
                                        <RequiredLabel>{t("label.name")}</RequiredLabel>
                                        <Input
                                            required={true}
                                            autoComplete={"off"}
                                            autoFocus={true}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                            invalid={fieldApi.state.meta.errors.length > 0}
                                        />
                                        {fieldApi.state.meta.errors.map((error) => (
                                            <ErrorMessage>{error}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>

                            <form.Field name={"description"}>
                                {(fieldApi) => (
                                    <Field>
                                        <Label>{t("label.description")}</Label>
                                        <Input
                                            autoComplete={"off"}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                        />
                                    </Field>
                                )}
                            </form.Field>

                            <form.Field name={"internet"}>
                                {(fieldApi) => (
                                    <SwitchField>
                                        <Label>{t("label.allow-exit-nodes")}</Label>
                                        <Description>{t("description.allow-exit-nodes")}</Description>
                                        <Switch
                                            color={"blue"}
                                            checked={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e)}
                                        />
                                    </SwitchField>
                                )}
                            </form.Field>

                            <DialogActions>
                                <Button plain={true} onClick={() => props.onClose()}>
                                    {tg("button.cancel")}
                                </Button>
                                <PrimaryButton type={"submit"}>{t("button.create-group")}</PrimaryButton>
                            </DialogActions>
                        </FieldGroup>
                    </Fieldset>
                </Form>
            </DialogBody>
        </Dialog>
    );
}
